import { gql } from '@apollo/client';
import { STYLE_SNAPSHOT_FIELDS } from './styleFactors';

/* ── the two factor views a fund and a portfolio share ─────────── */

/**
 * The regression loadings, as `factorExposures` and `benchmarkFactorExposures`
 * both answer them — on a scheme, a portfolio or a model alike.
 *
 * HOW TO READ THEM (the same on every subject):
 *   - every `*Score` is a TILT against the Top-500 index: subject − index, so
 *     0 is the market and + is more exposed than it;
 *   - `beta` is the RAW market beta (0.84), read against the index's own
 *     (`benchmarkFactorExposures.beta`, 1.0) — never added to it;
 *   - for every other factor, tilt + the index's loading = the subject's raw
 *     loading.
 * A portfolio is scored as equity, so its debt and gold pull tilts toward 0.
 *
 * Also spread by GET_FUND and GET_PORTFOLIO, so the list is written once.
 */
export const REGRESSION_EXPOSURE_FIELDS = `
  asOfDate beta alphaScore rSquared
  momentumScore valueScore qualityScore sizeScore growthScore
  shortTermMomentumScore longTermMomentumScore lowVolatilityScore highVolatilityScore
`;

/**
 * Everything the "Fundamental style" and "Regression exposures" tabs draw, on
 * whichever record carries it — `getMetaMutualFund` and `getPortfolio` both
 * answer these three fields with the same types, so one selection serves both
 * and one pair of components renders either.
 *
 * A plain string rather than a GraphQL fragment: a fragment is bound to one
 * type, and the cache runs with `addTypename: false` (see
 * CLASSIFICATION_SELECTION in portfolios.js), under which a fragment's fields
 * are silently dropped.
 *
 * `fundamentalStyleFactors` is what StyleFactors' snapshot reads — exposures,
 * the latest day's attribution and risk. Its numbers are z-scores (exposures)
 * and percentages already (attribution, risk).
 */
export const FACTOR_VIEW_FIELDS = `
  fundamentalStyleFactors { ${STYLE_SNAPSHOT_FIELDS} }
  factorExposures { ${REGRESSION_EXPOSURE_FIELDS} }
  benchmarkFactorExposures { securitySymbol ${REGRESSION_EXPOSURE_FIELDS} }
`;

/**
 * A scheme's factor views. Its own query, asked only when one of the two tabs
 * is open: the style snapshot carries a risk decomposition, and the fund page
 * has a dozen other tabs that do not need it.
 */
export const FUND_FACTOR_VIEW = gql`
  query FundFactorView($symbol: String!) {
    getMetaMutualFund(securitySymbol: $symbol) {
      securitySymbol
      ${FACTOR_VIEW_FIELDS}
    }
  }
`;

/**
 * A portfolio's (or goal's, or model's) factor views, asked only when one of
 * the two tabs is open rather than on every getPortfolio load. No history —
 * the tabs read one day. Refetched when the portfolio's analysis finishes
 * (config/jobTypes.js). `$forceRefresh` is for that moment alone — see
 * rereadPastBackendCache in hooks/_shared.js.
 */
export const PORTFOLIO_FACTOR_VIEW = gql`
  query PortfolioFactorView($symbol: String!, $forceRefresh: Boolean = false) {
    getPortfolio(securitySymbol: $symbol, includeHistorical: false, forceRefresh: $forceRefresh) {
      success
      message
      portfolio {
        securitySymbol
        ${FACTOR_VIEW_FIELDS}
      }
    }
  }
`;
